import {
  startOfMonth,
  isSameMonth,
  lastDayOfMonth,
  add,
  isFirstDayOfMonth,
  format,
} from "date-fns";
import { useSetAtom, Provider as AtomProvider } from "jotai";
import React, { useEffect, useState } from "react";

import { Box, HStack } from "@whoosh/design-system/src/components/primitive";

import { Calendar } from "../Calendar";
import { calendarSettingsAtom } from "../Calendar/atoms";
import {
  getFollowingMonthStart,
  getParsedDateObj,
} from "../Calendar/utils/calendarDateUtils";
import { MonthNavigation } from "./MonthNavigation";
import { InlineDatePickerProps } from "./types";

export const InlineDatePicker = (props: InlineDatePickerProps) => {
  return (
    <AtomProvider>
      <InlineDatePickerInner {...props} />
    </AtomProvider>
  );
};

const InlineDatePickerInner = ({
  startDate,
  endDate,
  onSelectDate,
  selectedDate,
  displayHorizontally,
  showDayOfWeekHeader,
  dateInfo,
  fillEmptyDays,
  hideOuterPadding,
  clubWeekStartsOn,
  onDisplayedMonthChange,
}: InlineDatePickerProps) => {
  const setCalendarSettings = useSetAtom(calendarSettingsAtom);

  useEffect(() => {
    setCalendarSettings({
      startDate: startDate,
      endDate: endDate,
      onDatePress: onSelectDate,
      fillEmptyDays: fillEmptyDays ?? true,
      dateInfo: dateInfo ?? null,
      singleDatePicker: true,
      clubWeekStartsOn: clubWeekStartsOn,
    });
  }, [
    clubWeekStartsOn,
    dateInfo,
    endDate,
    fillEmptyDays,
    onSelectDate,
    setCalendarSettings,
    startDate,
  ]);
  // this allows the month of the selectedDate to be shown by default if set
  const [firstDisplayedMonth, setFirstDisplayedMonth] = useState(
    getParsedDateObj(selectedDate || startDate, clubWeekStartsOn)
  );

  useEffect(() => {
    onDisplayedMonthChange?.(
      format(startOfMonth(firstDisplayedMonth), "yyyy-MM-dd", {
        weekStartsOn: clubWeekStartsOn,
      })
    );
  }, [clubWeekStartsOn, firstDisplayedMonth, onDisplayedMonthChange]);

  const calendarStartDate = getParsedDateObj(startDate, clubWeekStartsOn);
  const calendarEndDate = getParsedDateObj(endDate, clubWeekStartsOn);
  const nextMonthObj = getFollowingMonthStart(firstDisplayedMonth);

  const doesEndDateSpanToNextMonth = !isSameMonth(
    calendarEndDate,
    calendarStartDate
  );

  const firstMonthStartDate = isSameMonth(
    calendarStartDate,
    firstDisplayedMonth
  )
    ? calendarStartDate
    : startOfMonth(firstDisplayedMonth);

  const getLastDayOfFirstMonthCalendar = () => {
    const lastDayofFirstMonth = lastDayOfMonth(firstDisplayedMonth);
    if (isFirstDayOfMonth(firstMonthStartDate)) {
      return calendarEndDate < lastDayofFirstMonth
        ? calendarEndDate
        : lastDayofFirstMonth;
    }
    const fiveWeeksFromStartDate = add(firstMonthStartDate, { weeks: 4 });

    return calendarEndDate < fiveWeeksFromStartDate
      ? calendarEndDate
      : fiveWeeksFromStartDate < calendarEndDate &&
        fiveWeeksFromStartDate > lastDayofFirstMonth
      ? fiveWeeksFromStartDate
      : lastDayofFirstMonth;
  };

  const firstMonthEndDate = getLastDayOfFirstMonthCalendar();
  return (
    <Box
      backgroundColor="surface-primary"
      borderRadius="4"
      minWidth={displayHorizontally ? 685 : "auto"}
      testID="variable-week-calendar"
    >
      <MonthNavigation
        setFirstDisplayedMonth={setFirstDisplayedMonth}
        firstDisplayedMonth={firstDisplayedMonth}
        nextMonthObj={nextMonthObj}
        calendarStartDate={calendarStartDate}
        calendarEndDate={calendarEndDate}
        displayHorizontally={!!displayHorizontally}
        doesEndDateSpanToNextMonth={doesEndDateSpanToNextMonth}
        clubWeekStartsOn={clubWeekStartsOn}
      />
      <HStack justifyContent="space-between" testID="wrapper-around-months">
        <Calendar
          monthDateObj={firstDisplayedMonth}
          startDate={firstMonthStartDate}
          endDate={firstMonthEndDate}
          isLeft={displayHorizontally}
          showDayOfWeekHeader={showDayOfWeekHeader}
          fillEmptyDays={fillEmptyDays}
          showMonthNameHeader={false}
          clubWeekStartsOn={clubWeekStartsOn}
        />
        {!!displayHorizontally ? (
          <Calendar
            monthDateObj={nextMonthObj}
            startDate={getFollowingMonthStart(firstDisplayedMonth)}
            endDate={lastDayOfMonth(nextMonthObj)}
            isRight
            showDayOfWeekHeader={showDayOfWeekHeader}
            fillEmptyDays={fillEmptyDays}
            showMonthNameHeader={false}
            clubWeekStartsOn={clubWeekStartsOn}
          />
        ) : null}
      </HStack>
    </Box>
  );
};
